import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Package, Building2, Loader2, CornerDownLeft, ShoppingCart, ScrollText, Wallet, Factory, ShoppingBag, Ship } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { product: Package, customer: Building2, order: ShoppingCart, instrument: ScrollText, payment: Wallet, supplier: Factory, purchase_order: ShoppingBag, import: Ship };

function Results({ data, active, onPick }) {
  if (!data.groups.length) return <div className="px-4 py-6 text-sm text-slate-400 text-center" data-testid="global-search-empty">“{data.q}” için sonuç bulunamadı</div>;
  let idx = -1;
  return data.groups.map((g) => {
    const Icon = ICONS[g.type] || Search;
    return (
      <div key={g.type} data-testid={`global-search-group-${g.type}`}>
        <div className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{g.label}</div>
        {g.items.map((it) => {
          idx += 1;
          const i = idx;
          return (
            <button key={it.id} type="button" onMouseDown={(e) => { e.preventDefault(); onPick(it); }} data-testid={`global-search-item-${g.type}-${it.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${active === i ? "bg-blue-50" : "hover:bg-slate-50"}`}>
              <Icon className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-slate-800 truncate">{it.title}</div>
                <div className="text-[11px] font-mono text-slate-400 truncate">{it.subtitle}</div>
              </div>
              {active === i && <CornerDownLeft className="h-3.5 w-3.5 text-blue-600" />}
            </button>
          );
        })}
      </div>
    );
  });
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setData(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then(({ data }) => { setData(data); setActive(0); }).finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const flat = data ? data.groups.flatMap((g) => g.items) : [];
  const pick = (it) => { setOpen(false); setQ(""); inputRef.current?.blur(); navigate(it.url); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && flat[active]) { e.preventDefault(); pick(flat[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative flex-1 max-w-md" data-testid="global-search">
      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onKeyDown={onKey}
        className="w-full h-9 pl-9 pr-9 rounded-md border border-slate-200 bg-slate-50 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 focus:bg-white transition-colors"
        placeholder="Ürün, SKU, barkod, cari, cari kodu, telefon ara..." data-testid="global-search-input" />
      {loading && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-blue-600" />}
      {open && data && (
        <div className="absolute left-0 right-0 top-11 z-50 bg-white border border-slate-200 rounded-md shadow-xl max-h-[420px] overflow-y-auto pb-1 fade-up" data-testid="global-search-results">
          <Results data={data} active={active} onPick={pick} />
        </div>
      )}
    </div>
  );
}
